import { RefObject } from 'react';
import { View, ViewStyle } from 'react-native';

import { PlayFormat } from '../../../../services/AnalyticsService/Types';
import {
  KitPlayerProps,
  PlayerContainerDimensionsProps,
} from '../../KitPlayer.types';
import { MEDIA_TYPE, TogglePlayFunction } from '../../types';
import { PlayerPromptRef } from '../PlayerPrompt';
import { PlayerVideoRef } from '../PlayerVideo/PlayerVideo.types';
import { SettingsControlRef } from './PlayerControlTypes/DefaultControls/components/SettingsControl/SettingsControl.types';
import { PlayerControlTypes } from './PlayerControlTypes/types';
import { ActionIndicatorRef } from './components/ActionIndicator';

export interface PlayerTimelineProps {
  isVisible?: boolean;
  isShowingTooltipTime?: boolean;
  isFastSeeking?: boolean;
  fastSeekDirection?: 'forward' | 'backward';
  fastSeekMultiplierText?: string;
  bufferedTimeSeconds?: number;
  totalDurationSeconds?: number;
  onSeeking?: (seekTo: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  currentTimebarStyle?: ViewStyle;
  progressBarStyle?: ViewStyle;
  playheadStyle?: ViewStyle;
  playerVideoRef?: RefObject<PlayerVideoRef | null>;
  isLive?: boolean;
  // useLiveControls props needs to be passed to avoid of logic state duplication
  isWatchingLive?: boolean;
  setIsWatchingLive?: (isWatching: boolean) => void;
  goLive?: () => void;
  calculateIsWatchingLive?: (seekTime?: number) => void;
  setHasManuallySeekd?: (hasSeekd: boolean) => void;
}

export interface PlayerTimelineRef {
  fastSeekUpdate(seconds: number): void;
  updateTime(): void;
}

export interface PlayerControlsProps {
  actionIndicatorRef?: RefObject<ActionIndicatorRef | null>;
  analyticsFormat?: PlayFormat;
  branding?: KitPlayerProps['branding'];
  bufferedTimeSeconds?: number;
  containerRef?: RefObject<View | null>;
  playerContainerDimensions?: PlayerContainerDimensionsProps;

  /** Control overrides should be used minimally for special scenarios. */
  controlOverrides?: Partial<Record<PlayerControlTypes, boolean>>;
  controlType?: 'tv' | 'default';
  data: KitPlayerProps['data'];
  featureGiving?: boolean;
  featureInfo?: boolean;
  featureShareable?: boolean;
  hasAudio?: boolean;
  hasVideo?: boolean;
  info?: boolean;
  isFullscreen?: boolean;
  isLive?: boolean;
  isLoading?: boolean;
  isMenuOpen?: boolean;
  isPlayerReady?: boolean;
  isPlaying?: boolean;
  isPrePlayPosterShowing?: boolean;
  logoWatermark?: string | null;
  mediaType: MEDIA_TYPE;
  mediaUrl: string;
  playerPromptRef?: RefObject<PlayerPromptRef | null>;
  playerTimelineRef?: RefObject<PlayerTimelineRef | null>;
  playerVideoRef?: RefObject<PlayerVideoRef | null>;
  settingsControlRef?: RefObject<SettingsControlRef | null>;
  playId?: string;
  previousTimestamp?: number /** seconds */;
  totalDurationSeconds?: number | undefined;

  /** callbacks */
  onChangeMediaType?: (mediaType: MEDIA_TYPE) => void;
  onDismiss?: () => void;
  onFastSeekEnd?(): void;
  onFastSeekStart?(): void;
  onPlaybackSpeedChange?: (speed: number) => void;
  onSeekStart?: () => void;
  onSeekTo?: (seekTo: number | ((n: number) => number)) => void;
  onToggleFullscreen?(): void;
  onTogglePlay?: TogglePlayFunction;
}

export interface PlayerControlsRef {
  toggleControls(options?: { forceTo?: boolean }): void;
  skipForward(): void;
  skipBackward(): void;
  fastSeekForward(): void;
  fastSeekBackward(): void;
  stopFastSeeking(): void;
  incrementFastSeeking(): void;

  getIsFastSeeking(): boolean;
  getFastSeekingDirection(): 'forward' | 'backward';
  getAreControlsVisible(): boolean;
  getFocusedControl(): PlayerControlTypes | undefined;
}

export const CONTROLS_TIMEOUT = 2000;
export const SKIP_AMOUNT = 10;
export const FAST_SEEK_INTERVAL_MS = 30;
export const FAST_SEEK_SPEED_MULT_ARR = [10, 60, 240];
